import { Base2Presenter } from "core/presenter";
import { CompanyRepository, NewSaleReportRepository } from "core/repository";
import Moment from "moment";
import * as FileSaver from "file-saver";
import { fn } from "util";
import Big from "big.js";

class MultilocalitySalesReportPresenter extends Base2Presenter {
  initialState() {
    const urlParams = new URLSearchParams(window.location.hash);
    const startDate = Moment(urlParams.get("inicio"), "DD-MM-YYYY");
    const endDate = Moment(urlParams.get("fim"), "DD-MM-YYYY");
    return {
      loading: {
        chartLoading: false,
        revenuesLoading: false,
        dataTableLoading: false,
        defaultLoading: false
      },
      filters: {
        dateFrom: startDate.isValid()
          ? startDate
          : Moment()
              .startOf("day")
              .subtract(7, "days"),
        dateTo: endDate.isValid() ? endDate : Moment().endOf("day"),
        company: { name: "", uuid: "" }
      },
      pagination: {
        page: 1,
        perPage: 100,
        total: 0
      },
      order: {
        field: "company_name",
        direction: "DESC"
      },
      results: [],
      chartData: [],
      totalRevenues: [],
      companySearchOptions: {
        repository: CompanyRepository,
        order: [{ field: "slug", direction: "ASC" }],
        pagination: { page: 1, perPage: 25 },
        filter: filter => this.companyFilters(filter)
      }
    };
  }

  async onStart() {
    await this.load();
  }

  async setLoading(value) {
    this.enqueue(() => ({
      loading: {
        chartLoading: value,
        revenuesLoading: value,
        dataTableLoading: value,
        defaultLoading: value
      }
    }));
  }

  cleanResults() {
    this.enqueue(() => ({
      results: [],
      chartData: [],
      totalRevenues: []
    }));
  }

  async load() {
    this.renewCancelToken();
    this.cleanResults();
    this.setLoading(true);
    const order = this.getOrder();
    const filters = this.getFilters();
    const results = await NewSaleReportRepository.getMultilocalityReport({
      ...filters,
      order
    });
    if (results.success) {
      this.enqueue(vm => ({
        results: results.data.companies,
        chartData: results.data.companies,
        totalRevenues: results.data.total_revenues || 0,
        pagination: {
          ...vm.pagination,
          total: results.data.pagination.total_items,
          perPage: results.data.pagination.per_page
        }
      }));
    }
    this.setLoading(false);
  }

  getFilters() {
    const { dateFrom, dateTo, company } = this.viewModel.filters;

    return {
      company_uuid: company.uuid.length ? company.uuid : undefined,
      from: dateFrom.startOf("day").toISOString(),
      to: dateTo.endOf("day").toISOString()
    };
  }

  async companyFilters(company) {
    const filters = [];

    if (company) {
      const currentUser = await this.mRuntime.get("currentUser");
      filters.push({
        connector: "AND",
        field: "slug",
        operation: "LIKE",
        value: company
      });
      filters.push({
        connector: "AND",
        field: "account_uuid",
        operation: "EQ",
        value: currentUser.account_uuid
      });
      filters.push({
        connector: "OR",
        field: "fiscal_informations.cnpj",
        operation: "LIKE",
        value: company
      });
      filters.push({
        connector: "OR",
        field: "fiscal_informations.legal_name",
        operation: "LIKE",
        value: company
      });
    }
    return filters;
  }

  getOrder() {
    const { order } = this.viewModel;
    return order.direction === "DESC" ? `-${order.field}` : order.field;
  }

  onOrderChange(order, value) {
    this.enqueue({ order: { direction: value, field: order } }, () =>
      this.load()
    );
  }

  onPageChange(page) {
    this.enqueue(
      vm => ({ pagination: { ...vm.pagination, page } }),
      () => this.load()
    );
  }

  onFilterChange(filters) {
    if (filters.dateFrom && filters.dateTo) {
      window.location.hash = `?&inicio=${Moment(filters.dateFrom).format(
        "DD-MM-YYYY"
      )}&fim=${Moment(filters.dateTo).format("DD-MM-YYYY")}`;
    }
    for (const key in filters) {
      if (filters[key] !== this.viewModel.filters[key]) {
        this.enqueue(
          vm => ({
            filters: { ...vm.filters, ...filters }
          }),
          () => this.load()
        );

        break;
      }
    }
  }

  async onDownload(format) {
    this.enqueue(vm => ({ loading: { ...vm.loading, defaultLoading: true } }));

    switch (format) {
      case "csv":
        await this._downloadCSV();
        break;

      default:
        this.sendNotification({
          type: "error",
          title: "Oops...",
          content: "Ainda não suportamos esse formato"
        });
    }

    this.enqueue(vm => ({ loading: { ...vm.loading, defaultLoading: false } }));
  }

  async _loadDownload() {
    this.renewCancelToken();
    const order = this.getOrder();
    const filters = this.getFilters();
    filters.count = 99999;
    const results = await NewSaleReportRepository.getMultilocalityReport({
      ...filters,
      order
    });
    if (results.success) {
      return results.data.companies;
    }
    return [];
  }

  async _downloadCSV() {
    const results = await this._loadDownload();
    let content = [
      "Nome da Empresa; Quantidade de Vendas; Média de Produtos por Venda; Total de Taxa de Serviço; Total de desconto; Ticket Médio; Faturamento"
    ];
    results.forEach(item => {
      content.push(
        `${item.company_name};${item.sales_quantity};${Big(
          item.quantity_per_sale
        ).toFixed(2)};${fn.centsToReal(item.service_total)};${fn.centsToReal(
          item.discount
        )};${fn.centsToReal(item.ticket)};${fn.centsToReal(
          item.revenues_value
        )}`
      );
    });
    const contentBlob = new Blob([content.join("\n")], {
      type: "application/csv;charset=utf-8"
    });
    FileSaver.saveAs(contentBlob, "relatorio-de-vendas-multilojas.csv");
  }
}

export default MultilocalitySalesReportPresenter;



// WEBPACK FOOTER //
// ./src/components/app/management/multilocality-sales-report/presenter.js